// ============================================================================
// /build-room/connect — the one new screen of the Claude connector (item #853,
// picks 1A 2B 3A 4A; drawn to the approved demo's tab 3).
//
// Supabase's sign-in server sends Ben here (Site URL + this path) with an
// authorization_id when he presses Connect on claude.ai. He sees who is signed
// in, what Claude can see and do, and Cancel / Allow. Allow sends him back to
// Claude, connected; Cancel tells Claude no.
//
// Rendered OUTSIDE the shell (PLAYBOOK §18): <PublicPage><ForceLight>. Signed
// out, it shows the app's own sign-in, and the authorization_id stays in the
// address, so he comes back to this same screen. Anyone who is not a superuser
// gets the command center's own refusal, word for word. The lists below say what
// the connector's tools do (build-room-connector/connector.ts); the bridge, not
// this screen, decides who may act — only Ben (TCSD-0425/0426).
// ============================================================================
import { isSuperuser } from '../../shared/role-gates';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import { useAuth } from '../../shared/auth';
import { SignInScreen } from '../../shared/nav-shell';
import { Button, EmptyState } from '../../shared/components';
import type { Read } from './api';
import { answerAuthorization, readAuthorization, type AuthorizationDetails } from './connect-api';
import { CAN_DO, CAN_SEE } from './connect-words';

function Frame({ children }: { children: React.ReactNode }) {
  return <div className="flex min-h-full items-center justify-center bg-tcs-bg-muted p-4">{children}</div>;
}

export default function ConnectPage() {
  const { user, profile, loading, signIn, error } = useAuth();
  const [params] = useSearchParams();
  const id = params.get('authorization_id') ?? '';
  const superuser = isSuperuser(profile);

  const [details, setDetails] = useState<Read<AuthorizationDetails> | null>(null);
  const [busy, setBusy] = useState<'approve' | 'deny' | null>(null);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    if (!superuser || !id) return;
    void readAuthorization(id).then((r) => {
      // Already allowed for this app: the server answers with where to go.
      if (r.ok && r.data.redirect_url) window.location.assign(r.data.redirect_url);
      else setDetails(r);
    });
  }, [superuser, id]);

  const answer = async (action: 'approve' | 'deny') => {
    setBusy(action);
    setFailed(null);
    const r = await answerAuthorization(id, action);
    if (r.ok) {
      window.location.assign(r.data.redirect_url);
      return;
    }
    setBusy(null);
    setFailed(r.message);
  };

  if (loading) {
    return (
      <Frame>
        <p className="text-sm text-tcs-text-muted">Loading session…</p>
      </Frame>
    );
  }
  if (!user) return <SignInScreen onSignIn={signIn} error={error} />;
  if (!profile) {
    return (
      <Frame>
        <p className="text-sm text-tcs-text-muted">Loading session…</p>
      </Frame>
    );
  }
  // Same refusal, same words, as the command center page.
  if (!superuser) {
    return (
      <Frame>
        <EmptyState
          className="w-full max-w-md"
          title="Superusers only"
          body="The command center shows the build board and is gated to superusers."
        />
      </Frame>
    );
  }
  if (!id) {
    return (
      <Frame>
        <EmptyState
          className="w-full max-w-md"
          title="Nothing to connect"
          body="This screen opens from Claude, when you connect the TCS command center there."
        />
      </Frame>
    );
  }

  const clientName = details?.ok ? (details.data.client?.client_name ?? details.data.client?.name ?? 'Claude') : 'Claude';
  const email = (details?.ok ? details.data.user?.email : null) ?? profile.email ?? user.email ?? '';
  const name = profile.display_name ?? email;

  return (
    <Frame>
      <div className="w-full max-w-sm rounded-xl border border-tcs-border bg-tcs-bg p-8 shadow-sm">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-tcs-inverse text-tcs-inverse-ink">
            <Building2 className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-base font-semibold text-tcs-text">TCS App</h1>
            <p className="font-mono text-[10px] uppercase tracking-wide text-tcs-text-muted">Build Room · connect</p>
          </div>
        </div>

        <div className="mt-5 flex items-center gap-2.5 rounded-lg border border-tcs-border bg-tcs-bg-muted px-3 py-2.5 text-sm">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-tcs-surface text-xs font-semibold text-tcs-text-secondary">
            {(name || '?').slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0 truncate">
            <span className="font-semibold text-tcs-text">{name}</span>
            {email && name !== email && <span className="text-tcs-text-secondary"> · {email}</span>}
          </div>
        </div>

        {details === null && <p className="mt-5 text-sm text-tcs-text-muted">Reading what {clientName} is asking for…</p>}
        {details && !details.ok && (
          <div className="mt-5 rounded-md border border-st-danger-border bg-st-danger-bg px-3 py-2 text-sm text-rag-red">{details.message}</div>
        )}
        {details?.ok && (
          <>
            <p className="mt-5 text-[15px] font-semibold leading-snug text-tcs-text">{clientName} wants to use the command center for you.</p>
            <div className="mt-4">
              <h2 className="font-mono text-[10px] uppercase tracking-wide text-tcs-text-muted">See</h2>
              <ul className="mt-1.5 list-disc space-y-0.5 pl-5 text-sm text-tcs-text-secondary">
                {CAN_SEE.map((l) => <li key={l}>{l}</li>)}
              </ul>
            </div>
            <div className="mt-4">
              <h2 className="font-mono text-[10px] uppercase tracking-wide text-tcs-text-muted">Act — only Ben can, and a merge still goes through the merge gate</h2>
              <ul className="mt-1.5 list-disc space-y-0.5 pl-5 text-sm text-tcs-text-secondary">
                {CAN_DO.map((l) => <li key={l}>{l}</li>)}
              </ul>
            </div>
            {failed && (
              <div className="mt-4 rounded-md border border-st-danger-border bg-st-danger-bg px-3 py-2 text-sm text-rag-red">{failed}</div>
            )}
            <div className="mt-6 flex gap-2">
              <Button variant="secondary" className="flex-1" onClick={() => void answer('deny')} loading={busy === 'deny'} disabled={busy !== null}>
                Cancel
              </Button>
              <Button className="flex-1" onClick={() => void answer('approve')} loading={busy === 'approve'} disabled={busy !== null}>
                Allow
              </Button>
            </div>
          </>
        )}
        <p className="mt-6 text-center text-[11px] text-tcs-text-muted">You can disconnect it any time in Claude's Connectors settings.</p>
      </div>
    </Frame>
  );
}
